
import { Component, Vue } from 'vue-property-decorator'
import AdminPermissionForm from '@/components/admin/AdminPermissionForm.vue'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import catalogsService from '@/services/CatalogsService'
import { CrudActions, CatalogGridItemColumn, PermissionCatalogFilters, PermissionCatalogGridItem, Permission, BasicEntity, DomainCatalogFilters, Domain } from '@/types/Catalogs'
import { PageRequest } from '@/types/Support'
import userInfoStore from '@/store/userInfo'
import { PermissionNames } from '@/types/Auth'
import DateFormat from '@/services/DateFormat'
import LogService, { ToString } from '@/services/support/LogService'

@Component({
  components: {
    AdminPermissionForm,
    MessageError,
    TopToolBarPaginator
  }
})
export default class AdminPermissions extends Vue {
  PAGE_SIZE = 500
  PAGE_SIZE_VISIBLE_ITEMS = 50
  itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
  paginatorKey = 0
  crudActions = CrudActions
  currentAction = CrudActions.READ
  loadingData = false
  leftSidebarCollapsed = false
  // Grid columns definition
  currentPage = 1
  perPage = 20
  permissionColumns: CatalogGridItemColumn[] = [
    CatalogGridItemColumn.def('id', 'ID', true),
    CatalogGridItemColumn.def('name', 'Nombre', true),
    CatalogGridItemColumn.def('domain', 'Dominio', true),
    CatalogGridItemColumn.def('resource', 'Recurso', true),
    CatalogGridItemColumn.def('effect', 'Efecto', true),
    CatalogGridItemColumn.def('label', 'Etiqueta', true),
    CatalogGridItemColumn.def('version', 'Versión', true),
    CatalogGridItemColumn.def('description', 'Descripción', true),
    CatalogGridItemColumn.def('creation', 'Fecha de creación', true),
    CatalogGridItemColumn.def('update', 'Fecha de actualización', true)
  ]
  items: PermissionCatalogGridItem[] = []
  filteredItems: PermissionCatalogGridItem[] = []
  filters = new PermissionCatalogFilters()
  /** Filters */
  totalItems: number = 0
  selectedItem = new PermissionCatalogGridItem()
  columnOptions: string[] = []
  currentColumnOption = ''
  searchOptionsSelected: string[] = []
  searchOptionsCreated: string[] = []
  searchNames: string[] = []
  searchDomains: string[] = []
  searchResources: string[] = []
  searchEffects: string[] = []
  searchLabels: string[] = []
  searchBy = ''

  created () {
    this.updateData()
    this.columnOptions = this.filters.columnOptions
    this.currentColumnOption = this.filters.defaultSearchOptions
    if (this.canFilterByDomain) {
      this.loadDomains(new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE))
    }
  }

  /* Sort */

  get effectOptionsSorted () {
    return this.filters.effectFilters.sort(this.sortFilters)
  }

  get resourceOptionsSorted () {
    return this.filters.resourceFilters.sort(this.sortFilters)
  }

  sortFilters (a: {text: string, value: string}, b: {text: string, value: string}) {
    if (a.text < b.text) {
      return -1
    } else if (a.text > b.text) {
      return 1
    }
    return 0
  }

  /* User permissions */

  /**
   * Checks if user has 'permission' permission
   * @param permission name to check
   */
  hasPermission (permision: string) {
    return userInfoStore.userInfo.role.permissions.some(it => it.name === permision)
  }

  /**
   * Check if user has 'permissions:create' permission
   */
  get readPermission () {
    return this.hasPermission(PermissionNames.PERMISSIONS_READ)
  }

  /**
   * Check if user has 'permissions:create' permission
   */
  get createPermission () {
    return this.hasPermission(PermissionNames.PERMISSIONS_CREATE)
  }

  /**
   * Check if user has 'permissions:update' permission
   */
  get updatePermission () {
    return this.hasPermission(PermissionNames.PERMISSIONS_UPDATE)
  }

  /**
   * Check if user has 'permissions:delete' permission
   */
  get deletePermission () {
    return this.hasPermission(PermissionNames.PERMISSIONS_DELETE)
  }

  /**
   * Checks if the user can view domain filter, for this
   * the user must belongs to root domain and has domains read permission.
   */
  get canFilterByDomain () {
    return userInfoStore.inRootDomain() && this.hasPermission(PermissionNames.DOMAINS_READ)
  }

  /* Load data */

  /**
   * Clear and update all permission records
   */
  async updateData () {
    if (this.loadingData) return
    this.loadingData = true
    this.items = []
    const pageRequest = new PageRequest<PermissionCatalogFilters>(this.PAGE_SIZE)
    pageRequest.filters = this.filters
    await this.loadPage(pageRequest)
  }

  /**
   * Get all permission records and assign them in 'items' variable
   * @param pageRequest - configurations to request
   */
  async loadPage (pageRequest: PageRequest<any>) {
    await catalogsService.getPermissions(pageRequest).then(response => {
      this.items.push(...response.results.map((it: Permission): PermissionCatalogGridItem => {
        return this.fillGridItem(it, new PermissionCatalogGridItem())
      }))
      if (this.items.length < response.total) {
        pageRequest.page++
        this.loadPage(pageRequest)
      } else {
        this.loadingData = false
        this.filteredItems = this.items
      }
    }).catch((error) => {
      LogService.error(`Error en la carga de permisos: ${ToString(error.response ?? error)}`)
      this.loadingData = false
    })
  }

  /**
   * Gets all domains records and assing them in 'domains' variable
   * @param pageRequest - request configurations
   */
  async loadDomains (pageRequest: PageRequest<any>) {
    this.loadingDomains = true
    await catalogsService.getDomains(pageRequest).then((response) => {
      this.domains.push(...response.results.map((it: Domain) => {
        return { id: it.id, name: it.name } as BasicEntity
      }))
      if (this.domains.length < response.total) {
        pageRequest.page++
        this.loadDomains(pageRequest)
      }
    }).finally(() => {
      this.loadingDomains = false
      this.selectDefaultDomain()
    })
  }

  /* Grid and action events */

  /**
   * Select the current item on double clic.
   * @param item - row selected on table
   */
  selectItemAndShowDetail (item: any) {
    this.currentAction = CrudActions.READ_DETAIL
    this.selectItem(item)
  }

  /**
   * Select the current item and assign it in 'selectedItem' variable.
   * @param item - row selected on table
   */
  selectItem (item: any) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.DELETE:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.updatePermission ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        break
    }
  }

  /**
   * Check if 'item' is the current item selected
   * @param item - row table
   */
  isItemSelected (item: any) {
    return this.selectedItem === item
  }

  /** Clear the current selected item */
  cleanItemSelected () {
    this.selectedItem = new PermissionCatalogGridItem()
  }

  /**
   * This triggers a form view transition event to read permission
   */
  queryItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.READ_DETAIL
    }
  }

  /**
   * This triggers a form view transition event to create permission
   */
  newItem () {
    this.selectedItem = new PermissionCatalogGridItem()
    this.currentAction = CrudActions.CREATE
  }

  /**
   * This triggers a form view transition event to update permission
   */
  updateItem () {
    if (this.selectedItem.id) {
      this.currentAction = CrudActions.UPDATE
    }
  }

  /**
   * Delete the current permission selected after a confirmation message.
   */
  deleteItem () {
    if (this.selectedItem.id) {
      let messageConfirmation = `Esta por eliminar el registro:\
        "${this.selectedItem.id} - ${this.selectedItem.name}".\
        Esta acción es irreversible y se perderá permanentemente dicho registro.\
        ¿Estás seguro de continuar?`
      this.$bvModal.msgBoxConfirm(messageConfirmation, {
        title: 'Eliminando permiso',
        size: 'md',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'Confirmar',
        cancelVariant: 'outline-secondary',
        cancelTitle: 'Cancelar',
        headerClass: 'p-2 border-bottom-0',
        footerClass: 'p-2 border-top-0',
        hideHeaderClose: false
      }).then(value => {
        if (value) {
          catalogsService.deletePermissionById(this.selectedItem.id).then((response) => {
            this.$bvToast.toast(`El permiso ${this.selectedItem.id} se ha eliminado correctamente`, {
              title: 'Eliminación finalizada',
              variant: 'success',
              toaster: 'b-toaster-top-center',
              autoHideDelay: 3000
            })
          }).catch((error) => {
            let messageValues = {
              title: 'Error al eliminar el permiso',
              message: '',
              delay: 10000,
              error: error
            }
            let errorComponent = new MessageError({ propsData: messageValues })
            errorComponent.displayMessage()
          }).finally(() => {
            this.updateData()
            this.currentAction = CrudActions.READ
          })
        }
      })
    }
  }

  /**
   * Create a PermissionCatagolGridItem with base on Permission
   * @param origin - to obtains values
   * @param target - PermissionCatalogGridItem to fill with origin object values
   */
  fillGridItem (origin: Permission, target: PermissionCatalogGridItem) {
    target.id = origin.id
    target.name = origin.name
    target.domain = origin.domain
    target.resource = origin.resource
    target.effect = origin.effect
    target.label = origin.label
    target.version = origin.version
    target.description = origin.description
    target.creation = origin.creationDate ? new DateFormat(origin.creationDate).toDateString() : '- - / - - / - - - -'
    target.update = origin.updateDate ? new DateFormat(origin.updateDate).toDateString() : '- - / - - / - - - -'
    return target
  }

  /**
   * Switch to edit mode
   */
  changeToEditMode () {
    this.currentAction = CrudActions.UPDATE
  }

  /* Filter and sorting data */

  /** Calculates visible index range for pagination */
  onPageChange (page: number) {
    this.itemsVisibleRange = [(page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS, (page - 1) * this.PAGE_SIZE_VISIBLE_ITEMS + this.PAGE_SIZE_VISIBLE_ITEMS]
    this.cleanItemSelected()
  }

  /** Reset index range pagination and clear current selected item */
  resetPaginator () {
    this.itemsVisibleRange = [0, this.PAGE_SIZE_VISIBLE_ITEMS]
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
  }

  /**
   * Clear current filter configurations
   */
  cleanFilter () {
    this.filters = new PermissionCatalogFilters()
    this.filteredItems = this.items
    this.searchOptionsSelected = []
    this.searchOptionsCreated = []
    this.searchNames = []
    this.searchDomains = []
    this.searchResources = []
    this.searchEffects = []
    this.searchLabels = []
    this.searchBy = ''
  }

  /**
   * Apply all filters configurations
   */
  async filterItems () {
    this.loadingData = true
    this.resetPaginator()
    if (this.filters.domain !== this.domainIdSelected) {
      this.filters.domain = this.domainIdSelected
      await this.updateData()
    }
    this.applyAllFilters()
    this.loadingData = false
  }

  /** Check if the index of a row is within the visible range  */
  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.itemsVisibleRange[0] && rowIndex < this.itemsVisibleRange[1]
  }

  /**
   * Returns the current number of filtered items
   */
  get countTotalItems () {
    return this.filteredItems.length
  }

  /**
   * Sort current filtered items with base a specific column
   * @param col - column to filter with
   */
  sortItems (col: CatalogGridItemColumn) {
    catalogsService.sortItems(this.filteredItems, col)
  }

  /**
   * Assign column to create filter tags
   * @param tag - field available option clicled
   */
  searchByTag (tag: string) {
    this.currentColumnOption = tag
  }

  /**
   * Create a new tag with base on current culumn to filter
   * and search value introduced, it is pushed on 'searchOptionsCreated'
   */
  createTag () {
    let newTag = this.currentColumnOption + ':' + this.searchBy
    if (this.searchBy && !this.searchOptionsCreated.includes(newTag)) {
      if (!this.searchOptionsSelected.includes(this.currentColumnOption)) {
        this.searchOptionsSelected.push(this.currentColumnOption)
      }
      this.searchOptionsCreated.push(newTag)
      switch (this.currentColumnOption) {
        case 'Name':
          this.searchNames.push(this.searchBy)
          break
        case 'Domain':
          this.searchDomains.push(this.searchBy)
          break
        case 'Resource':
          this.searchResources.push(this.searchBy)
          break
        case 'Effect':
          this.searchEffects.push(this.searchBy)
          break
        case 'Label':
          this.searchLabels.push(this.searchBy)
          break
      }
      this.searchBy = ''
    }
  }

  /**
   * Remove tag from 'searchOptionsCreated' array,
   * that is considered to apply filters
   */
  removeTag (tag: string) {
    let field = tag.split(':')[0]
    let value = tag.split(':')[1]
    this.searchOptionsCreated.splice(this.searchOptionsCreated.indexOf(tag), 1)
    switch (field) {
      case 'Name':
        this.searchNames.splice(this.searchNames.indexOf(value), 1)
        this.deleteFromSelectedOptions(field, this.searchNames.length <= 0)
        break
      case 'Domain':
        this.searchDomains.splice(this.searchDomains.indexOf(value), 1)
        this.deleteFromSelectedOptions(field, this.searchDomains.length <= 0)
        break
      case 'Resource':
        this.searchResources.splice(this.searchResources.indexOf(value), 1)
        this.deleteFromSelectedOptions(field, this.searchResources.length <= 0)
        break
      case 'Effect':
        this.searchEffects.splice(this.searchEffects.indexOf(value), 1)
        this.deleteFromSelectedOptions(field, this.searchEffects.length <= 0)
        break
      case 'Label':
        this.searchLabels.splice(this.searchLabels.indexOf(value), 1)
        this.deleteFromSelectedOptions(field, this.searchLabels.length <= 0)
        break
    }
  }

  /**
   * Delete a field value from 'searchOptionsSelected' array
   * if 'searchOptionsCreated' does not contains a value like 'field:_' or notValues
   * @param field - value to eliminate from 'searchOptionsSelected'
   * @param notValues - indicates that option must eliminated cause has not existences
   */
  deleteFromSelectedOptions (field: string, notValues: boolean) {
    let exitsFilter = false
    this.searchOptionsCreated.forEach(filter => {
      if (filter.split(':')[0] === field) {
        exitsFilter = true
      }
    })
    if (!exitsFilter || notValues) {
      this.searchOptionsSelected.splice(this.searchOptionsSelected.indexOf(field), 1)
    }
  }

  /**
   * Apply all filters created on 'filteredItems'
   */
  applyAllFilters () {
    let nameExpressions: RegExp[] = []
    let domainExpressions: RegExp[] = []
    let resourceExpressions: RegExp[] = []
    let effectExpressions: RegExp[] = []
    let labelExpressions: RegExp[] = []
    this.filteredItems = this.items
    if (this.searchNames.length > 0) {
      nameExpressions = this.createRegularExpressions(this.searchNames)
      this.filteredItems = this.filteredItems.filter(it => this.applyFilters('name', it, nameExpressions))
    }
    if (this.searchDomains.length > 0) {
      domainExpressions = this.createRegularExpressions(this.searchDomains)
      this.filteredItems = this.filteredItems.filter(it => this.applyFilters('domain', it, domainExpressions))
    }
    if (this.searchResources.length > 0) {
      resourceExpressions = this.createRegularExpressions(this.searchResources)
      this.filteredItems = this.filteredItems.filter(it => this.applyFilters('resource', it, resourceExpressions))
    }
    if (this.searchEffects.length > 0) {
      effectExpressions = this.createRegularExpressions(this.searchEffects)
      this.filteredItems = this.filteredItems.filter(it => this.applyFilters('effect', it, effectExpressions))
    }
    if (this.searchLabels.length > 0) {
      labelExpressions = this.createRegularExpressions(this.searchLabels)
      this.filteredItems = this.filteredItems.filter(it => this.applyFilters('label', it, labelExpressions))
    }
    // Basic filters
    if (this.filters.versionSelected.length > 0) {
      this.filteredItems = this.filteredItems.filter(it => this.filters.versionSelected.includes(it.version))
    }
    if (this.filters.effectSelected.length > 0) {
      this.filteredItems = this.filteredItems.filter(it => this.filters.effectSelected.includes(it.effect))
    }
    if (this.filters.resourceSelected.length > 0) {
      this.filteredItems = this.filteredItems.filter(it => this.filters.resourceSelected.includes(it.resource))
    }
  }

  /**
   * Create a regular expressions with base on values string array
   * @param values - strings
   * @returns regular expression array
   */
  createRegularExpressions (values: string[]) {
    let expressions: RegExp[] = []
    values.forEach((val) => {
      expressions.push(new RegExp(val, 'i'))
    })
    return expressions
  }

  /**
   * Apply all regular expressions to a permission grid item
   * over a specific field
   * @param field - field to apply expressions over
   * @param permission - permissions to check expressions
   * @param expressions - all reg exp that applies
   * @returns true if some expression passes on field, false otherwie
   */
  applyFilters (field: string, permission: PermissionCatalogGridItem, expressions: RegExp[]) {
    let apply = false
    expressions.forEach(expression => {
      if (field === 'name' && expression.test(permission.name)) {
        apply = true
      }
      if (field === 'domain' && expression.test(permission.domain.toString())) {
        apply = true
      }
      if (field === 'resource' && expression.test(permission.resource)) {
        apply = true
      }
      if (field === 'effect' && expression.test(permission.effect)) {
        apply = true
      }
      if (field === 'label' && expression.test(permission.label)) {
        apply = true
      }
    })
    return apply
  }

  /**
   * Returns distinct fields to 'currentColumnOption'
   */
  get fieldOptionsAvailable () {
    return this.filters.columnOptions.filter((tag) =>
      (tag !== this.currentColumnOption))
  }

  /* Domain selector */

  domainSelectionOpen = false
  domainIdSelected = -1
  domainSelected: any = {}
  domainSelectedBackup: any = {}
  searchDomain = ''
  domains: BasicEntity[] = []
  loadingDomains = false

  /**
   * Active domainSelectionOpen flag to display the dropdown domain list
   * and updates the searchDomain and domainSelected values when the dropdown is close
   * @param isOpen flag
   */
  showDomainSelection (isOpen: boolean) {
    if (isOpen) {
      this.searchDomain = ''
      this.domainSelected = {}
      this.domainSelectionOpen = true
    } else {
      setTimeout(() => {
        this.domainSelectionOpen = false
        if (this.domainSelectedBackup.name && this.domainSelectedBackup.id !== this.domainSelected.id) {
          this.searchDomain = this.domainSelectedBackup.name
          this.domainSelected = this.domainSelectedBackup
        }
      }, 200)
    }
  }

  /**
   * Validates if a domain name matches with the current searchDomain value
   * @param domain entity to check its name
   */
  isMatchDomain (domain: BasicEntity) {
    if (!this.searchDomain) return true
    const regexSearch = new RegExp(this.searchDomain, 'i')
    return regexSearch.test(domain.name)
  }

  /**
   * Updates domains variables based on 'domain' selected
   * @param domain selected
   */
  selectDomain (domain: BasicEntity) {
    this.domainSelected = domain
    this.domainSelectedBackup = domain
    this.searchDomain = domain.name
    this.domainIdSelected = domain.id
  }

  /**
   * Sets the current user domain as default domain selected
   */
  selectDefaultDomain () {
    this.filters.domain = userInfoStore.userInfo.domain.id
    this.domainIdSelected = userInfoStore.userInfo.domain.id
    const domain = this.domains.find(it => it.id === this.domainIdSelected)
    if (domain) this.selectDomain(domain)
  }

  /* Form */

  onCancelForm () {
    this.currentAction = CrudActions.READ
  }

  onItemSaved (item: Permission) {
    if (!this.selectedItem.id) {
      this.selectedItem = new PermissionCatalogGridItem()
      this.selectedItem.id = item.id
      this.items.unshift(this.selectedItem)
    }
    this.fillGridItem(item, this.selectedItem)
    this.currentAction = CrudActions.READ
  }

  /* Export data */

  printCsv () {
    let csv = ''
      .concat('ID').concat(',')
      .concat('NAME').concat(',')
      .concat('DOMAIN').concat(',')
      .concat('RESOURCE').concat(',')
      .concat('EFFECT').concat(',')
      .concat('LABEL').concat(',')
      .concat('VERSION').concat(',')
      .concat('DESCRIPTION').concat(',')
      .concat('CREACIÓN').concat(',')
      .concat('ACTUALIZACIÓN').concat('\n')

    for (let it of this.filteredItems) {
      csv += ''
        .concat(it.id.toString()).concat(',')
        .concat(it.name).concat(',')
        .concat(it.domain.toString()).concat(',')
        .concat(it.resource).concat(',')
        .concat(it.effect).concat(',')
        .concat(it.label).concat(',')
        .concat(it.version.toString()).concat(',')
        .concat(it.description).concat(',')
        .concat(it.creation).concat(',')
        .concat(it.update).concat('\n')
    }

    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `Permissions.csv`
    element.click()
  }
}
